import { ArrowRight, Plus } from "lucide-react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import type { Highlight } from "../../data/content";
import type { Product } from "../../types";
import { formatPrice } from "../../utils/format";
import { Badge } from "../ui/Badge";

const cardClass =
  "group flex h-full flex-col overflow-hidden rounded-card border border-line bg-card transition-colors duration-300 hover:border-primary/40";

const hoverLift = {
  whileHover: { y: -8 },
  transition: { type: "spring", stiffness: 300, damping: 22 },
} as const;

interface FeaturedCardProps {
  product: Product;
  quantity: number;
  onAdd: () => void;
}

/** Card escuro de "Mais vendidos" com botão "+" circular. */
export const FeaturedCard = ({ product, quantity, onAdd }: FeaturedCardProps) => (
  <motion.article {...hoverLift} className={cardClass}>
    <div className="relative aspect-[4/3] overflow-hidden bg-night">
      <img
        src={product.image_url || "/favicon.png"}
        alt={product.name}
        loading="lazy"
        className="size-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
      />
      <Badge variant="dark" className="absolute left-3 top-3">
        {product.category}
      </Badge>
    </div>
    <div className="flex flex-1 flex-col p-5">
      <h3 className="font-display text-[1.75rem] leading-none tracking-wide">{product.name}</h3>
      <p className="mt-2 line-clamp-2 text-sm text-muted">{product.description}</p>
      <div className="mt-auto flex items-center justify-between pt-5">
        <span className="text-xl font-bold text-primary">{formatPrice(product.price)}</span>
        <motion.button
          type="button"
          whileTap={{ scale: 0.85 }}
          onClick={onAdd}
          aria-label={`Adicionar ${product.name} ao carrinho`}
          className="btn-glow relative grid size-11 place-items-center rounded-full bg-primary text-night transition-colors hover:bg-primary-hover"
        >
          <Plus className="size-5" strokeWidth={2.5} aria-hidden="true" />
          {quantity > 0 && (
            <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full border-2 border-card bg-white px-1 text-[10px] font-bold text-night">
              {quantity}
            </span>
          )}
        </motion.button>
      </div>
    </div>
  </motion.article>
);

/** Versão estática (API fora do ar): mesmos destaques da versão anterior. */
export const HighlightCard = ({ item }: { item: Highlight }) => (
  <motion.article {...hoverLift} className={cardClass}>
    <div className="relative aspect-[4/3] overflow-hidden bg-night">
      <img
        src={item.image_url}
        alt={item.name}
        loading="lazy"
        className="size-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
      />
    </div>
    <div className="flex flex-1 flex-col p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">{item.category}</p>
      <h3 className="mt-1 font-display text-[1.75rem] leading-none tracking-wide">{item.name}</h3>
      <Link
        to="/cardapio"
        className="mt-auto inline-flex items-center gap-2 pt-5 text-sm font-semibold text-primary hover:underline"
      >
        Ir para Cardápio <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </div>
  </motion.article>
);
